import { useCallback, useEffect, useMemo, useRef, type ClipboardEvent } from 'react';
import { type CellKeyDownEvent, type ColDef, type GridApi, type GridReadyEvent, type ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { X } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { LinhaSimuladaResponse, PerdaModo } from '@triade/contracts';
import { useEdicaoDeGrade } from '@/lib/edicao-pendente';
import { formatarCusto, formatarTextoDigitado } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ContextoGrade } from './grade-comum';
import { SO_AO_SALVAR } from './erros-salvar';
import { EditorComponente, type ValorComponente } from './editor-componente';
import type { ItemResponse } from '@triade/contracts';
import { atualizarSemEditar, Chip, comMarcaDeErro, ctx, DIREITA, EditorCentro, Etiqueta, rotuloCentro, setter, TEMA } from './grade-comum';
import {
  aplicarColagem,
  COLUNAS_EDITAVEIS,
  COLUNAS_HORAS,
  fraseConsumo,
  lerColagem,
  linhaComItem,
  motivoDoAviso,
  PERDA_MODOS,
  ROTULO_ALERTA,
  ROTULO_AVISO_LINHA,
  ROTULO_PERDA_MODO,
  ROTULO_TIPO_COMPONENTE,
  seloComponente,
  textoCampoDecimal,
  travadaNaGrade,
  type CampoEdicao,
  type CentroOpcao,
  type ColunaEditavel,
  type LinhaFicha,
  type TipoComponente,
} from './tipos';

const ROTULO_RESTRICAO = 'Mão de obra fora do processo';
const SEM_FORNECEDORES: ReadonlyArray<{ id: string; nome: string }> = [];

/** `selo: false` = sem o selo do tipo (composicao de horas: todas as linhas sao mao de obra). */
type ParamsComponente = ICellRendererParams<LinhaFicha> & { selo?: boolean };

/** Descricao do material com o selo do tipo, a etiqueta do item do cadastro, o link da ficha (semiacabado/coproduto) e os alertas da API. */
function Componente(p: ParamsComponente) {
  const codigo = p.data?.itemCodigo;
  const sim: LinhaSimuladaResponse | undefined = p.data ? ctx(p).calculo?.get(p.data.uid) : undefined;
  const alertas = (sim?.alertas ?? []).filter((a) => !SO_AO_SALVAR.has(a));
  const avisos = (sim?.avisos ?? []).filter((a) => !SO_AO_SALVAR.has(a));
  const restricoes = sim?.restricoes ?? [];
  const frase = p.data ? fraseConsumo(p.data) : null;
  const selo = p.data && p.selo !== false ? seloComponente(p.data) : null;
  const fichaLink = p.data && travadaNaGrade(p.data) ? p.data.fichaId : null;
  return (
    <span className="flex min-w-0 flex-col justify-center">
      <span className="flex min-w-0 items-center gap-2">
        {selo && (
          <span
            data-testid="selo-componente"
            className={cn('shrink-0 rounded-full px-2 text-caption leading-5 font-medium', selo.destaque ? 'bg-primary/10 text-primary' : 'bg-line-soft text-ink-body')}
          >
            {selo.rotulo}
          </span>
        )}
        {codigo && <Etiqueta title="Item do cadastro">{codigo}</Etiqueta>}
        <span className="truncate">{p.value}</span>
        {fichaLink && (
          <Link to="/custos/fichas-tecnicas/$id" params={{ id: fichaLink }} className="shrink-0 text-caption font-medium text-primary hover:underline">
            Abrir ficha
          </Link>
        )}
        {(alertas.length > 0 || avisos.length > 0 || restricoes.length > 0) && (
          <span className="flex shrink-0 flex-wrap items-center gap-1" data-testid="alertas-linha">
            {restricoes.map((r) => (
              <Chip key={r} tom="erro" testid="restricao-linha">
                {ROTULO_RESTRICAO}
              </Chip>
            ))}
            {alertas.map((a) => (
              <Chip key={a} tom={a === 'PERDA_INVALIDA' ? 'erro' : 'aviso'}>
                {ROTULO_ALERTA[a]}
              </Chip>
            ))}
            {avisos.map((a) => {
              // Sessao 49: semiacabado sem custo mostra o motivo da subficha (insumo e item) no lugar do rotulo generico; sem motivo, o rotulo.
              const motivo = motivoDoAviso(sim, a);
              return (
                <Chip
                  key={a}
                  tom={a === 'CPM_ANTIGO' ? 'aviso' : 'erro'}
                  testid={motivo ? 'motivo-subficha' : undefined}
                  titulo={motivo}
                  className={motivo ? 'max-w-[28rem] truncate' : undefined}
                >
                  {motivo ?? ROTULO_AVISO_LINHA[a]}
                </Chip>
              );
            })}
          </span>
        )}
      </span>
      {frase && (
        <span className="truncate text-caption text-ink-muted" data-testid="frase-consumo">
          {frase}
        </span>
      )}
    </span>
  );
}

/** Centro de consumo (opcional, informativo): caminho e nome; vazio = nenhum. */
function CentroConsumo(p: ICellRendererParams<LinhaFicha>) {
  const id = p.data?.centroConsumoId;
  if (!id) return <span className="text-ink-disabled">—</span>;
  const c = ctx(p).centros.find((x) => x.id === id);
  if (!c) return <span className="text-danger-ink">Centro indisponível</span>;
  // Sessao 37: o centro de consumo tem de ser o de uma etapa desta ficha; se a etapa saiu, a linha avisa (a API recusa ao salvar).
  const fora = ctx(p).opcoesCentro !== undefined && !ctx(p).opcoesCentro!.some((x) => x.id === id);
  return (
    <span className="flex min-w-0 items-center gap-2" title={fora ? `${rotuloCentro(c)} — sem etapa neste processo` : rotuloCentro(c)} data-testid={fora ? 'consumo-fora-das-etapas' : undefined}>
      <Etiqueta>{c.caminho}</Etiqueta>
      <span className={fora ? 'truncate text-danger-ink' : 'truncate'}>{c.nome}</span>
      {fora && <span className="whitespace-nowrap text-caption text-danger-ink">sem etapa</span>}
    </span>
  );
}

function Remover(p: ICellRendererParams<LinhaFicha>) {
  if (!p.data || !ctx(p).editavel) return null;
  const uid = p.data.uid;
  const n = (p.node.rowIndex ?? 0) + 1;
  return (
    <button
      type="button"
      aria-label={`Remover linha ${n}`}
      title="Remover linha"
      tabIndex={-1}
      onClick={() => ctx(p).remover(uid)}
      className="inline-flex size-7 items-center justify-center rounded-md text-ink-muted hover:bg-line-soft hover:text-danger-ink"
    >
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
}

/** Edicao de um material: AQUISICAO nao aceita custo digitado (o do motor); centro de consumo e opcional. */
export function aplicarEdicaoMaterial(linha: LinhaFicha, campo: CampoEdicao, valor: string, centros: ReadonlyArray<CentroOpcao>): LinhaFicha {
  if (campo === 'tipo') {
    return { ...linha, tipo: valor as TipoComponente, ...(valor === 'AQUISICAO' ? { custoUnitario: '' } : {}) };
  }
  // Perda e quantidade mudam o consumo: o consumo gravado (e a frase) deixa de valer ate salvar.
  if (campo === 'perdaModo') {
    return { ...linha, perdaModo: (PERDA_MODOS.includes(valor as PerdaModo) ? valor : 'NENHUM') as PerdaModo, perdaValor: valor === 'NENHUM' ? '' : linha.perdaValor, consumoTotal: undefined };
  }
  if (campo === 'perdaValor') return { ...linha, perdaValor: valor, consumoTotal: undefined };
  if (campo === 'quantidade') return { ...linha, quantidade: valor, consumoTotal: undefined };
  if (campo === 'centroConsumoId') {
    if (!valor) return { ...linha, centroConsumoId: null };
    const c = centros.find((x) => x.id === valor);
    return c ? { ...linha, centroConsumoId: c.id } : linha;
  }
  if (campo === 'centroCustoId') return linha; // material nao tem centro de custo: a etapa e da secao Processo
  if (campo === 'fornecedorId') return { ...linha, fornecedorId: valor || null };
  return { ...linha, [campo]: valor };
}

/** Rotulos da composicao de horas (SERVICO): a mesma grade, com as colunas de horas no lugar das de material. */
const ROTULO_HORAS: Record<string, string> = { descricao: 'Profissional ou centro', quantidade: 'Horas', custoUnitario: 'Custo por hora' };
const COLUNAS_COMPOSICAO = ['descricao', 'quantidade', 'custoUnitario', 'custoTotal', 'acoes'] as const;

/**
 * Colunas da secao Materiais. `comCusto` = custo.ver: as colunas de R$ nem existem para o PCP. Ordem de Tab: tipo, componente,
 * quantidade, unidade, custo; o centro de consumo (opcional) vem depois dos numeros. Composicao de horas (SERVICO): so
 * componente (profissional ou centro), horas, custo por hora e total.
 */
function colunas(comCusto: boolean, comPerda: boolean, comFornecedor: boolean, composicaoHoras: boolean): ColDef<LinhaFicha>[] {
  // Semiacabado e coproduto nao sao editados na grade (o custo vem da ficha deles / do rateio).
  const editavel = (p: { context: unknown; data?: LinhaFicha }) => ctx(p).editavel && !(p.data && travadaNaGrade(p.data));
  const todas: ColDef<LinhaFicha>[] = [
    {
      colId: 'tipo',
      headerName: 'Tipo',
      width: 98,
      editable: editavel,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: (p: { context: unknown }) => ({ values: ctx(p).tipos ?? [] }),
      valueGetter: (p) => p.data?.tipo,
      valueFormatter: (p) => ROTULO_TIPO_COMPONENTE[p.value as TipoComponente] ?? String(p.value ?? ''),
      valueSetter: setter('tipo'),
    },
    {
      colId: 'descricao',
      headerName: 'Componente',
      width: 280,
      minWidth: 280,
      editable: editavel,
      valueGetter: (p) => p.data?.descricao,
      // Linha de material/aquisicao: o editor busca no cadastro de itens e troca o componente; o resto (horas, mao de obra, outro) segue texto livre.
      ...(composicaoHoras
        ? {}
        : {
            // Enter e setas ficam com o editor (lista de itens); o resto das teclas segue o padrao da grade.
            suppressKeyboardEvent: (p: { editing: boolean; event: KeyboardEvent; data?: LinhaFicha }) =>
              p.editing && !!p.data && (p.data.tipo === 'MATERIAL' || p.data.tipo === 'AQUISICAO') && ['Enter', 'ArrowDown', 'ArrowUp'].includes(p.event.key),
            cellEditorSelector: (p: { data?: LinhaFicha }) => (p.data && (p.data.tipo === 'MATERIAL' || p.data.tipo === 'AQUISICAO') ? { component: EditorComponente } : undefined),
          }),
      valueSetter: (p) => {
        const v = p.newValue as ValorComponente | null | undefined;
        if (v && typeof v === 'object' && 'item' in v) ctx(p).escolherItem?.(p.data.uid, v.item);
        else ctx(p).editar(p.data.uid, 'descricao', v == null ? '' : String(v));
        return false;
      },
      cellRenderer: Componente,
      cellRendererParams: { selo: !composicaoHoras },
    },
    {
      colId: 'quantidade',
      headerName: 'Qtd.',
      width: 80,
      editable: editavel,
      valueGetter: (p) => p.data?.quantidade,
      valueSetter: setter('quantidade'),
      ...DIREITA,
    },
    {
      colId: 'unidade',
      headerName: 'Un.',
      width: 60,
      editable: editavel,
      valueGetter: (p) => p.data?.unidade,
      valueSetter: setter('unidade'),
    },
    {
      colId: 'custoUnitario',
      headerName: 'Custo unit.',
      width: 106,
      editable: (p) => editavel(p) && p.data?.tipo !== 'AQUISICAO',
      // Digitado, ou o que a API resolveu (insumo com ficha escolhida, custo de aquisicao). Semiacabado/coproduto: o da ficha.
      valueGetter: (p) => {
        const l = p.data;
        if (!l) return '';
        if (travadaNaGrade(l)) return l.custoFicha?.custoUnitario ? textoCampoDecimal(l.custoFicha.custoUnitario) : '';
        if (l.custoUnitario) return l.custoUnitario;
        const sim = ctx(p).calculo?.get(l.uid);
        return sim?.custoUnitario ? textoCampoDecimal(sim.custoUnitario) : '';
      },
      valueSetter: setter('custoUnitario'),
      valueFormatter: (p) => formatarTextoDigitado(p.value == null ? '' : String(p.value), 'custo'),
      tooltip: (p) => (p.data && !p.data.custoUnitario && p.value ? 'Custo resolvido pela API' : undefined),
      ...DIREITA,
      cellClass: (p) => cn('ag-right-aligned-cell tabular', p.data && !p.data.custoUnitario && 'italic text-ink-muted'),
    },
    {
      colId: 'fornecedor',
      headerName: 'Fornecedor',
      width: 130,
      editable: (p) => editavel(p) && p.data?.tipo === 'AQUISICAO',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: (p: { context: unknown }) => ({ values: ['', ...(ctx(p).fornecedores ?? []).map((f) => f.id)] }),
      valueGetter: (p) => (p.data?.tipo === 'AQUISICAO' ? (p.data.fornecedorId ?? '') : null),
      valueFormatter: (p) => {
        if (p.value === null) return '—';
        if (!p.value) return 'Preferencial';
        return (ctx(p).fornecedores ?? []).find((f) => f.id === p.value)?.nome ?? 'Fornecedor indisponível';
      },
      valueSetter: setter('fornecedorId'),
      tooltip: () => 'Opcional: fixa o fornecedor desta compra. Vazio = fornecedor preferencial do item.',
    },
    {
      colId: 'perdaModo',
      headerName: 'Perda',
      width: 150,
      editable: editavel,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: PERDA_MODOS },
      valueGetter: (p) => p.data?.perdaModo ?? 'NENHUM',
      valueFormatter: (p) => ROTULO_PERDA_MODO[p.value as PerdaModo] ?? '',
      valueSetter: setter('perdaModo'),
      tooltip: () => 'Perda da linha: o consumo total sai do modo e do valor. Sobre a saída: 1 + p. Sobre a entrada: 1 ÷ (1 − p). Rendimento: 1 ÷ r.',
    },
    {
      colId: 'perdaValor',
      headerName: 'Valor',
      width: 72,
      editable: (p) => editavel(p) && (p.data?.perdaModo ?? 'NENHUM') !== 'NENHUM',
      valueGetter: (p) => p.data?.perdaValor ?? '',
      valueSetter: setter('perdaValor'),
      ...DIREITA,
      tooltip: (p) => (p.data?.perdaModo === 'CONSUMO_TOTAL' ? 'Consumo total em quantidade (decimal)' : 'Percentual, ex.: 20'),
    },
    {
      colId: 'centroConsumo',
      headerName: 'Centro de consumo',
      width: 136,
      editable: editavel,
      cellEditor: EditorCentro,
      valueGetter: (p) => p.data?.centroConsumoId ?? null,
      valueSetter: setter('centroConsumoId'),
      cellRenderer: CentroConsumo,
      tooltip: () => 'Opcional: onde o material é usado. Não entra no custo.',
    },
    {
      colId: 'custoTotal',
      headerName: 'Total',
      width: 118,
      suppressNavigable: true,
      valueGetter: (p) => {
        if (p.data && travadaNaGrade(p.data)) return p.data.custoFicha?.custoTotal ?? '—';
        const sim = p.data ? ctx(p).calculo?.get(p.data.uid) : undefined;
        if (!sim) return '…';
        return sim.custoTotal === null || sim.custoTotal === undefined ? '—' : sim.custoTotal;
      },
      valueFormatter: (p) => (p.value === '…' || p.value === '—' ? String(p.value) : formatarCusto(String(p.value))),
      ...DIREITA,
      cellClass: 'ag-right-aligned-cell tabular font-medium text-ink',
    },
    { colId: 'acoes', headerName: '', width: 36, suppressNavigable: true, cellRenderer: Remover },
  ];
  const semR$ = new Set(['custoUnitario', 'custoTotal']);
  const perda = new Set(['perdaModo', 'perdaValor']);
  // As colunas de perda so aparecem quando ha perda numa linha ou o usuario pede (a grade cabe a 1280px sem elas).
  // Fornecedor: so com custo.ver e com material comprado na grade (a grade cabe a 1280px sem ela).
  const materiais = todas.filter(
    (c) =>
      (comCusto || !semR$.has(c.colId ?? '')) &&
      (comPerda || !perda.has(c.colId ?? '')) &&
      (c.colId !== 'fornecedor' || (comCusto && comFornecedor)),
  );
  if (!composicaoHoras) return materiais;
  // Composicao: sem tipo, unidade (sempre horas), perda, fornecedor nem centro de consumo. Sem R$ para o PCP (custo/hora some).
  return materiais
    .filter((c) => (COLUNAS_COMPOSICAO as readonly string[]).includes(c.colId ?? ''))
    .map((c) => (c.colId && ROTULO_HORAS[c.colId] ? { ...c, headerName: ROTULO_HORAS[c.colId] } : c));
}

export interface GradeMateriaisProps {
  /** So as linhas da secao Materiais. */
  linhas: LinhaFicha[];
  calculo: ReadonlyMap<string, LinhaSimuladaResponse>;
  editavel: boolean;
  comCusto: boolean;
  /** Tipos aceitos pelo item (produto nao aceita mao de obra fora do processo). */
  tipos: TipoComponente[];
  centros: ReadonlyArray<CentroOpcao>;
  /**
   * Centros que o seletor de centro de consumo oferece (Sessao 37): SO os que ja estao nas etapas do processo desta ficha (a API recusa outro).
   * Sem isto, oferece todos (`centros`).
   */
  centrosDeConsumo?: ReadonlyArray<CentroOpcao>;
  /** Celulas a marcar por linha (uid) apos a validacao ao salvar. */
  erros?: ReadonlyMap<string, ReadonlySet<string>>;
  /** Item dono da ficha: nao aparece na busca do editor de Componente. */
  itemIdDaFicha?: string;
  onChange: (linhas: LinhaFicha[]) => void;
  focar?: { uid: string; n: number } | null;
  apiRef?: React.MutableRefObject<GridApi<LinhaFicha> | null>;
  /** Mostra as colunas Perda e Valor mesmo sem perda nas linhas. */
  mostrarPerda?: boolean;
  /** Fornecedores para a coluna Fornecedor (so com custo.ver). */
  fornecedores?: ReadonlyArray<{ id: string; nome: string }>;
  /** Composicao de horas do SERVICO: linhas de mao de obra em horas, sem centro, sem fornecedor nem perda. */
  composicaoHoras?: boolean;
}

/**
 * Secao "Materiais" (AG Grid Community, planilha): item, centro de consumo opcional, quantidade, unidade, custo unitario e
 * total. O total e o custo da linha como a API simulou (sem R$ para o PCP). Nada e calculado aqui.
 * Com `composicaoHoras` (SERVICO): a mesma grade para as horas de cada profissional ou equipe (ver `colunas`).
 */
export function GradeMateriais({
  linhas,
  calculo,
  editavel,
  comCusto,
  tipos,
  centros,
  centrosDeConsumo,
  erros,
  itemIdDaFicha,
  onChange,
  focar,
  apiRef,
  mostrarPerda = false,
  fornecedores = SEM_FORNECEDORES,
  composicaoHoras = false,
}: GradeMateriaisProps) {
  const api = useRef<GridApi<LinhaFicha> | null>(null);
  const edicaoGrade = useEdicaoDeGrade();
  const linhasRef = useRef(linhas);
  linhasRef.current = linhas;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const contexto = useMemo<ContextoGrade>(
    () => ({
      calculo,
      editavel,
      centros,
      ...(centrosDeConsumo ? { opcoesCentro: centrosDeConsumo } : {}),
      ...(erros ? { erros } : {}),
      ...(itemIdDaFicha ? { itemIdDaFicha } : {}),
      escolherItem: (uid: string, item: ItemResponse) => onChangeRef.current(linhasRef.current.map((l) => (l.uid === uid ? linhaComItem(l, item) : l))),
      tipos,
      fornecedores,
      editar: (uid: string, campo: CampoEdicao, valor: string) =>
        onChangeRef.current(linhasRef.current.map((l) => (l.uid === uid ? aplicarEdicaoMaterial(l, campo, valor, centrosDeConsumo ?? centros) : l))),
      remover: (uid: string) => onChangeRef.current(linhasRef.current.filter((l) => l.uid !== uid)),
    }),
    [calculo, editavel, centros, centrosDeConsumo, erros, itemIdDaFicha, tipos, fornecedores],
  );

  const comPerda = !composicaoHoras && (mostrarPerda || linhas.some((l) => (l.perdaModo ?? 'NENHUM') !== 'NENHUM'));
  const comFornecedor = !composicaoHoras && linhas.some((l) => l.tipo === 'AQUISICAO');
  const defs = useMemo(() => comMarcaDeErro(colunas(comCusto, comPerda, comFornecedor, composicaoHoras)), [comCusto, comPerda, comFornecedor, composicaoHoras]);

  useEffect(() => {
    atualizarSemEditar(api.current, ['custoUnitario', 'custoTotal', 'descricao']);
  }, [calculo]);

  useEffect(() => {
    if (!api.current) return;
    api.current.setGridOption('context', contexto);
    atualizarSemEditar(api.current);
  }, [contexto]);

  useEffect(() => {
    if (!focar || !api.current) return;
    const id = setTimeout(() => {
      const a = api.current;
      if (!a) return;
      const indice = linhasRef.current.findIndex((l) => l.uid === focar.uid);
      if (indice < 0) return;
      a.ensureIndexVisible(indice);
      a.setFocusedCell(indice, 'descricao');
      a.startEditingCell({ rowIndex: indice, colKey: 'descricao' });
    }, 0);
    return () => clearTimeout(id);
  }, [focar]);

  // A coluna da descricao (Etapa / Componente) leva o espaco que sobra no wrapper, com minimo de 280px. Largura por
  // setColumnWidths (e nao flex): o AG Grid nao recalcula colunas flex quando a janela muda, e a ultima coluna sairia do cartao.
  const largura = useRef<HTMLDivElement>(null);
  const ajustarDescricao = useCallback(() => {
    const a = api.current;
    const alvo = largura.current;
    if (!a || !alvo) return;
    const fixas = (a.getColumns() ?? []).filter((c) => c.getColId() !== 'descricao').reduce((soma, c) => soma + c.getActualWidth(), 0);
    a.setColumnWidths([{ key: 'descricao', newWidth: Math.max(280, alvo.clientWidth - fixas - 2) }]);
  }, []);
  useEffect(() => {
    const alvo = largura.current;
    if (!alvo || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(() => ajustarDescricao());
    observador.observe(alvo);
    return () => observador.disconnect();
  }, [ajustarDescricao]);
  useEffect(() => ajustarDescricao(), [defs, ajustarDescricao]);

  const aoTeclar = useCallback(
    (e: CellKeyDownEvent<LinhaFicha>) => {
      const ev = e.event as KeyboardEvent | null;
      if (!ev || !editavel || ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (ev.key !== 'Delete' && ev.key !== 'Backspace') return;
      if (e.api.getEditingCells().length > 0) return;
      const col = e.column.getColId();
      if (!e.data || travadaNaGrade(e.data) || col === 'tipo' || !(COLUNAS_EDITAVEIS as readonly string[]).includes(col)) return;
      if (col === 'custoUnitario' && e.data.tipo === 'AQUISICAO') return;
      ev.preventDefault();
      contexto.editar(e.data.uid, col as ColunaEditavel, '');
    },
    [contexto, editavel],
  );

  const aoColar = (e: ClipboardEvent<HTMLDivElement>) => {
    const a = api.current;
    if (!editavel || !a || a.getEditingCells().length > 0) return;
    const foco = a.getFocusedCell();
    const col = foco?.column.getColId() as ColunaEditavel | undefined;
    const colunasColagem: readonly string[] = composicaoHoras ? COLUNAS_HORAS : COLUNAS_EDITAVEIS;
    if (!foco || !col || !colunasColagem.includes(col)) return;
    const matriz = lerColagem(e.clipboardData.getData('text/plain'));
    if (matriz.length === 0) return;
    e.preventDefault();
    const colado = aplicarColagem(linhas, foco.rowIndex, col, matriz, composicaoHoras ? 'horas' : 'materiais');
    // Linha de semiacabado/coproduto nao muda com a colagem: volta como estava.
    onChange(colado.map((l, i) => {
      const original = linhas[i];
      return original && travadaNaGrade(original) ? original : l;
    }));
  };

  const aoPronta = (e: GridReadyEvent<LinhaFicha>) => {
    api.current = e.api;
    if (apiRef) apiRef.current = e.api;
    ajustarDescricao();
  };

  return (
    <div ref={largura} data-testid="grade-materiais" onPaste={aoColar} className="w-full">
      <AgGridReact<LinhaFicha>
        theme={TEMA}
        rowData={linhas}
        columnDefs={defs}
        context={contexto}
        getRowId={(p) => p.data.uid}
        onGridReady={aoPronta}
        {...edicaoGrade}
        onCellKeyDown={aoTeclar}
        domLayout="autoHeight"
        suppressColumnVirtualisation
        enterNavigatesVertically
        enterNavigatesVerticallyAfterEdit
        stopEditingWhenCellsLoseFocus
        suppressMovableColumns
        suppressCellFocus={false}
        tooltipShowDelay={400}
        defaultColDef={{ sortable: false, resizable: false, suppressHeaderMenuButton: true }}
      />
    </div>
  );
}
